import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Input, message, Popconfirm, Row, Spin, Typography, Upload } from 'antd';
import { DeleteOutlined, PictureOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  deleteBulletinLogo,
  deleteBulletinSignature,
  getBulletinBranding,
  getBulletinLogo,
  getBulletinSignature,
  getBulletinProfesseurPrincipalSignature,
  updateBulletinBranding,
  uploadBulletinLogo,
  uploadBulletinSignature,
  uploadBulletinProfesseurPrincipalSignature,
  deleteBulletinProfesseurPrincipalSignature,
} from './bulletin-branding.reducer';

const { Paragraph, Text, Title } = Typography;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

interface BrandingFormValues {
  headerText?: string;
  footerText?: string;
}

const validateLines = (_rule, value?: string) => {
  if (!value || value.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').length <= 5) {
    return Promise.resolve();
  }
  return Promise.reject(new Error('Maximum 5 lignes'));
};

export const BulletinBranding = (props: RouteComponentProps<{ ecoleid: string }>) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<BrandingFormValues>();
  const ecoleId = props.match.params.ecoleid;
  const branding = useAppSelector(state => state.bulletinBranding.entity);
  const logoPreview = useAppSelector(state => state.bulletinBranding.logoPreview);
  const signaturePreview = useAppSelector(state => state.bulletinBranding.signaturePreview);
  const professeurPrincipalSignaturePreview = useAppSelector(state => state.bulletinBranding.professeurPrincipalSignaturePreview);
  const loading = useAppSelector(state => state.bulletinBranding.loading);
  const updating = useAppSelector(state => state.bulletinBranding.updating);
  const errorMessage = useAppSelector(state => state.bulletinBranding.errorMessage);

  useEffect(() => {
    dispatch(getBulletinBranding(ecoleId));
    dispatch(getBulletinLogo(ecoleId));
    dispatch(getBulletinSignature(ecoleId));
    dispatch(getBulletinProfesseurPrincipalSignature(ecoleId));
  }, [dispatch, ecoleId]);

  useEffect(() => {
    if (branding.ecoleId === ecoleId) {
      form.setFieldsValue({ headerText: branding.headerText || '', footerText: branding.footerText || '' });
    }
  }, [branding, ecoleId, form]);

  const saveTexts = async (values: BrandingFormValues) => {
    try {
      await dispatch(
        updateBulletinBranding({ ecoleId, headerText: values.headerText || null, footerText: values.footerText || null })
      ).unwrap();
      message.success('Paramètres du bulletin enregistrés');
    } catch (error) {
      message.error("Impossible d'enregistrer les paramètres du bulletin");
    }
  };

  const uploadImage = (kind: 'logo' | 'signature' | 'professeurPrincipalSignature') => ({
    async beforeUpload(file) {
      if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > MAX_IMAGE_SIZE) {
        message.error('Choisissez une image PNG ou JPEG de 5 Mo maximum');
        return false;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        if (kind === 'logo') {
          await dispatch(uploadBulletinLogo({ ecoleId, file: formData })).unwrap();
        } else if (kind === 'signature') {
          await dispatch(uploadBulletinSignature({ ecoleId, file: formData })).unwrap();
        } else {
          await dispatch(uploadBulletinProfesseurPrincipalSignature({ ecoleId, file: formData })).unwrap();
        }
        message.success(
          kind === 'logo'
            ? 'Logo enregistré'
            : kind === 'signature'
            ? 'Signature de la direction enregistrée'
            : 'Signature du professeur principal enregistrée'
        );
      } catch (error) {
        message.error("Impossible d'enregistrer l'image");
      }
      return false;
    },
    accept: 'image/png,image/jpeg',
    showUploadList: false,
  });

  const removeImage = async (kind: 'logo' | 'signature' | 'professeurPrincipalSignature') => {
    try {
      if (kind === 'logo') {
        await dispatch(deleteBulletinLogo(ecoleId)).unwrap();
      } else if (kind === 'signature') {
        await dispatch(deleteBulletinSignature(ecoleId)).unwrap();
      } else {
        await dispatch(deleteBulletinProfesseurPrincipalSignature(ecoleId)).unwrap();
      }
      message.success(
        kind === 'logo'
          ? 'Logo supprimé'
          : kind === 'signature'
          ? 'Signature de la direction supprimée'
          : 'Signature du professeur principal supprimée'
      );
    } catch (error) {
      message.error("Impossible de supprimer l'image");
    }
  };

  const imageCard = (
    title: string,
    alt: string,
    preview: string | null,
    present: boolean,
    kind: 'logo' | 'signature' | 'professeurPrincipalSignature'
  ) => (
    <Card title={title} style={{ height: '100%' }}>
      <div style={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        {preview ? (
          <img src={preview} alt={alt} style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }} />
        ) : (
          <div style={{ textAlign: 'center', color: '#8c8c8c' }}>
            <PictureOutlined style={{ fontSize: 48 }} />
            <div>Aucune image enregistrée</div>
          </div>
        )}
      </div>
      <Upload {...uploadImage(kind)}>
        <Button icon={<UploadOutlined />} loading={updating}>
          {present ? 'Remplacer' : 'Importer'}
        </Button>
      </Upload>{' '}
      <Popconfirm title="Supprimer cette image ?" onConfirm={() => removeImage(kind)} okText="Supprimer" cancelText="Annuler">
        <Button danger icon={<DeleteOutlined />} disabled={!present || updating}>
          Supprimer
        </Button>
      </Popconfirm>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">PNG ou JPEG, 5 Mo maximum</Text>
      </div>
    </Card>
  );

  return (
    <Spin spinning={loading}>
      <Title level={2}>Paramètres du bulletin</Title>
      {errorMessage && <Alert type="error" showIcon message="Une erreur est survenue" description={errorMessage} className="mb-3" />}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          {imageCard("Logo de l'école", "Logo de l'école", logoPreview, branding.logoPresent, 'logo')}
        </Col>
        <Col xs={24} lg={12}>
          {imageCard(
            'Signature du professeur principal',
            'Signature du professeur principal',
            professeurPrincipalSignaturePreview,
            branding.professeurPrincipalSignaturePresent,
            'professeurPrincipalSignature'
          )}
        </Col>
        <Col xs={24} lg={12}>
          {imageCard(
            'Signature et cachet de la direction',
            'Signature de la direction',
            signaturePreview,
            branding.signaturePresent,
            'signature'
          )}
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Textes du bulletin">
            <Form form={form} layout="vertical" onFinish={saveTexts}>
              <Form.Item label="En-tête de page" name="headerText" rules={[{ max: 500 }, { validator: validateLines }]}>
                <Input.TextArea rows={5} maxLength={500} showCount placeholder="Texte affiché au-dessus de l’en-tête standard" />
              </Form.Item>
              <Form.Item label="Pied de page" name="footerText" rules={[{ max: 500 }, { validator: validateLines }]}>
                <Input.TextArea rows={5} maxLength={500} showCount placeholder="Texte centré au bas de chaque page" />
              </Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={updating}>
                Enregistrer les textes
              </Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Aperçu du bulletin" style={{ height: '100%' }}>
            <div style={{ border: '1px solid #d9d9d9', minHeight: 330, padding: 20, background: '#fff' }}>
              <Paragraph style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{branding.headerText || 'En-tête personnalisé'}</Paragraph>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '20px 0' }}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Aperçu du logo" style={{ width: 70, maxHeight: 70, objectFit: 'contain' }} />
                ) : null}
                <div>
                  <Text strong>BULLETIN DE NOTES</Text>
                  <br />
                  <Text type="secondary">Nom de l’école · Année · Période · Classe</Text>
                </div>
              </div>
              <div style={{ height: 100, border: '1px solid #eee', padding: 12 }}>Tableau des résultats</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, textAlign: 'center' }}>
                <div>
                  <Text>Professeur principal</Text>
                  <br />
                  {professeurPrincipalSignaturePreview ? (
                    <img
                      src={professeurPrincipalSignaturePreview}
                      alt="Aperçu de la signature du professeur principal"
                      style={{ maxWidth: 130, maxHeight: 55, objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ height: 55 }} />
                  )}
                  <br />
                  <Text type="secondary">Signature</Text>
                </div>
                <div>
                  <Text>Direction</Text>
                  <br />
                  {signaturePreview ? (
                    <img
                      src={signaturePreview}
                      alt="Aperçu de la signature"
                      style={{ maxWidth: 130, maxHeight: 55, objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ height: 55 }} />
                  )}
                  <br />
                  <Text type="secondary">Cachet et signature</Text>
                </div>
              </div>
              <Paragraph style={{ whiteSpace: 'pre-line', textAlign: 'center', marginTop: 20, marginBottom: 0 }}>
                {branding.footerText || 'Pied de page personnalisé'}
              </Paragraph>
            </div>
          </Card>
        </Col>
      </Row>
    </Spin>
  );
};

export default BulletinBranding;
